/*
 * Form render functions specific for Mobile and Measurement Points
 */

import * as React from 'react';

import { Col, FormLabel, FormControl, FormGroup, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import Select, { components } from 'react-select';

import orderBy from 'lodash/orderBy';
import moment from 'moment';
import DatetimePortal from './DatetimePortal';
import { AbstractControl } from 'react-reactive-form';
import { MeasurementPointNoteButton } from '../measurementPointList/MeasurementPointNoteButton';
import NewMPNoteButton from '../measurementPointList/NewMeasurementPointNoteButton';
import { FormUtil, RequiredIndicator } from '../common/FormUtil';

const RichTextEditor = React.lazy(() => import('./RichTextEditor'));
const RTE = React.lazy(() => import('./TinyMCERichTextEditor'));

const hasHelpText = (helpText?: string) => !!helpText?.replace(/<[^>]*>/g, '').trim();

// add the bootstrap form-control class to the react-select select component
const ControlComponent = (props: any) => (
    <div>
        <components.Control {...props} className="form-control" />
    </div>
);

export const FormUtilMeasurementPoints = {
    text: (isMobile: boolean) => ({ handler, touched, meta, pristine, errors, submitted }: AbstractControl) => {
        const validationState = FormUtil.getValidationState(pristine, errors, submitted);
        const validationClass =
            validationState === 'success' ? 'has-success' : validationState === 'error' ? 'has-error' : '';
        return (
            <Col xs={meta.columnWidths.mainCol} className="mp-row">
                <FormGroup style={meta.style} className={validationClass}>
                    <Row className="vertical-align">
                        <Col
                            xs={meta.columnWidths.measurementPointCol}
                            style={{ paddingLeft: isMobile ? '0px' : undefined }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <FormLabel className="control-label mp-label">
                                    {meta.label}
                                    <RequiredIndicator meta={meta} />
                                </FormLabel>
                                {meta && meta.showLastValue && (
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <span className="control-label" style={{ textTransform: 'uppercase' }}>
                                            {meta.t ? meta.t('common:lastReading') : 'Last reading:'}{' '}
                                        </span>
                                        <span className="control-label" style={{ fontWeight: 'bold' }}>
                                            {meta.lastReadingData &&
                                            meta.lastReadingData.value !== null &&
                                            meta.lastReadingData.value !== undefined
                                                ? meta.lastReadingData.value
                                                : 'N/A'}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <FormControl
                                as={meta.componentClass}
                                type={meta.type || 'text'}
                                rows={meta.rows}
                                autoFocus={meta.autoFocus}
                                name={meta.name || ''}
                                isInvalid={validationState === 'error'}
                                isValid={validationState === 'success'}
                                {...handler()}
                            />
                            <FormControl.Feedback />
                        </Col>
                        {meta.guideText?.trim() && (
                            <Col className="mp-guide" xs={meta.columnWidths.guideCol}>
                                {meta.guideText}
                            </Col>
                        )}
                    </Row>
                    {hasHelpText(meta.helpText) && (
                        <Row>
                            <Col
                                xs={meta.columnWidths.measurementPointCol}
                                style={{ paddingLeft: isMobile ? '0px' : undefined }}
                                className="mp-help"
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: meta.helpText
                                    }}
                                />
                            </Col>
                        </Row>
                    )}
                </FormGroup>
            </Col>
        );
    },

    select: (isMobile: boolean) => ({
        handler,
        touched,
        meta,
        pristine,
        errors,
        submitted,
        value
    }: AbstractControl) => {
        const selectClassName = meta.isMulti ? 'is-multi beacon-select' : 'beacon-select';
        const validationState = FormUtil.getValidationState(pristine, errors, submitted);
        const selectValidationClass =
            validationState === 'success' ? 'has-success' : validationState === 'error' ? 'has-error' : '';
        const selectOptions = meta.disableSort
            ? meta.options
            : orderBy(meta.options, option => option.label.toLowerCase());
        return (
            <Col xs={meta.columnWidths.mainCol} className="mp-row">
                <FormGroup>
                    <Row className="vertical-align">
                        <Col
                            xs={meta.columnWidths.measurementPointCol}
                            style={{ paddingLeft: isMobile ? '0px' : undefined }}
                        >
                            <FormLabel className="mp-label">
                                {meta.label}
                                <RequiredIndicator meta={meta} />
                            </FormLabel>
                            <Select
                                options={selectOptions}
                                className={`${selectClassName} ${selectValidationClass}`}
                                components={{ Control: ControlComponent }}
                                placeholder={meta.placeholder}
                                isMulti={meta.isMulti}
                                classNamePrefix="react-select"
                                name={meta.name || ''}
                                isClearable={typeof meta.isClearable !== 'undefined' ? meta.isClearable : false}
                                isDisabled={handler().disabled}
                                inputId={meta.id}
                                {...handler()}
                            />
                        </Col>
                        {meta.guideText?.trim() && (
                            <Col className="mp-guide" xs={meta.columnWidths.guideCol}>
                                {meta.guideText}
                            </Col>
                        )}
                    </Row>
                    {hasHelpText(meta.helpText) && (
                        <Row>
                            <Col
                                xs={meta.columnWidths.measurementPointCol}
                                style={{ paddingLeft: isMobile ? '0px' : undefined }}
                                className="mp-help"
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: meta.helpText
                                    }}
                                />
                            </Col>
                        </Row>
                    )}
                </FormGroup>
            </Col>
        );
    },
    NewMeasurementPointNote: (props: AbstractControl) => <NewMPNoteButton {...props} />,
    MeasurementPointNote: (props: AbstractControl) => <MeasurementPointNoteButton {...props} />,
    group: ({ handler, meta }: any) => {
        return (
            <Col xs={meta.columnWidths.mainCol} className="mp-row">
                <Row>
                    <Col xs={12}>
                        <h4 className="mp-header">{meta.label}</h4>
                    </Col>
                </Row>
            </Col>
        );
    },
    passFail: (isMobile: boolean) => ({ handler, touched, meta, pristine, errors, submitted }: AbstractControl) => {
        const validationState = FormUtil.getValidationState(pristine, errors, submitted);
        const validationClass =
            validationState === 'success' ? 'has-success' : validationState === 'error' ? 'has-error' : '';

        return (
            <Col xs={meta.columnWidths.mainCol} className="mp-row">
                <FormGroup style={meta.style} className={validationClass}>
                    <Row className="vertical-align">
                        <Col xs={12} style={{ padding: isMobile ? '0px' : undefined }}>
                            <FormLabel className="control-label mp-label">
                                {meta.label}
                                <RequiredIndicator meta={meta} />
                            </FormLabel>
                            <ToggleButtonGroup
                                name="pass-fail"
                                {...handler()}
                                type="radio"
                                style={{ display: 'flex', maxWidth: '220px' }}
                            >
                                <ToggleButton value={1} variant="success" disabled={handler().disabled}>
                                    Pass
                                </ToggleButton>
                                <ToggleButton value={2} variant="danger" disabled={handler().disabled}>
                                    Fail
                                </ToggleButton>
                                <ToggleButton value={3} variant="primary" disabled={handler().disabled}>
                                    N/A
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Col>
                        {meta.guideText?.trim() && (
                            <Col className="mp-guide" xs={meta.columnWidths.guideCol}>
                                {meta.guideText}
                            </Col>
                        )}
                    </Row>
                    {hasHelpText(meta.helpText) && (
                        <Row>
                            <Col
                                xs={meta.columnWidths.measurementPointCol}
                                style={{ paddingLeft: isMobile ? '0px' : undefined }}
                                className="mp-help"
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: meta.helpText
                                    }}
                                />
                            </Col>
                        </Row>
                    )}
                </FormGroup>
            </Col>
        );
    },
    numeric: (isMobile: boolean) => ({ handler, touched, meta, pristine, errors, submitted }: AbstractControl) => {
        const validationState = FormUtil.getValidationState(pristine, errors, submitted);
        const validationClass =
            validationState === 'success' ? 'has-success' : validationState === 'error' ? 'has-error' : '';

        return (
            <Col xs={meta.columnWidths.mainCol} className="mp-row">
                <FormGroup style={meta.style} className={validationClass}>
                    <Row className="vertical-align">
                        <Col
                            xs={meta.columnWidths.measurementPointCol}
                            style={{ paddingLeft: isMobile ? '0px' : undefined }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <FormLabel className="control-label mp-label">
                                    {meta.label}
                                    <RequiredIndicator meta={meta} />
                                </FormLabel>
                                {meta && meta.showLastValue && (
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <span className="control-label" style={{ textTransform: 'uppercase' }}>
                                            {meta.t ? meta.t('common:lastReading') : 'Last reading:'}{' '}
                                        </span>
                                        <span className="control-label" style={{ fontWeight: 'bold' }}>
                                            {meta.lastReadingData &&
                                            meta.lastReadingData.value !== null &&
                                            meta.lastReadingData.value !== undefined
                                                ? meta.lastReadingData.value
                                                : 'N/A'}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <FormControl
                                placeholder={meta.placeholder}
                                as={meta.componentClass}
                                type="number"
                                rows={meta.rows}
                                autoFocus={meta.autoFocus}
                                name={meta.name || ''}
                                onWheel={(e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur()}
                                isInvalid={validationState === 'error'}
                                isValid={validationState === 'success'}
                                {...handler()}
                            />
                            <FormControl.Feedback />
                        </Col>
                        {meta.guideText?.trim() && (
                            <Col className="mp-guide" xs={meta.columnWidths.guideCol}>
                                {meta.guideText}
                            </Col>
                        )}
                    </Row>
                    {hasHelpText(meta.helpText) && (
                        <Row>
                            <Col
                                xs={meta.columnWidths.measurementPointCol}
                                style={{ paddingLeft: isMobile ? '0px' : undefined }}
                                className="mp-help"
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: meta.helpText
                                    }}
                                />
                            </Col>
                        </Row>
                    )}
                </FormGroup>
            </Col>
        );
    },
    date: (isMobile: boolean) => ({ handler, touched, meta, pristine, errors, submitted }: AbstractControl) => {
        const validationState = FormUtil.getValidationState(pristine, errors, submitted);
        const validationClass =
            validationState === 'success' ? 'has-success' : validationState === 'error' ? 'has-error' : '';

        const defaultValue = handler().value;
        const momentDate = !defaultValue || moment.isMoment(defaultValue) ? defaultValue : moment.utc(defaultValue);

        // Format date value for display
        const formatDateValue = (dateValue: any) => {
            if (!dateValue) return 'N/A';
            const momentValue = moment.isMoment(dateValue) ? dateValue : moment.utc(dateValue);
            return momentValue.isValid() ? momentValue.format('DD-MMM-YY') : 'N/A';
        };

        return (
            <Col xs={meta.columnWidths.mainCol} className="">
                <FormGroup className={`datetime-select ${validationClass} ${meta.alignRight && 'alignRight'}`}>
                    <Row className="vertical-align">
                        <Col
                            xs={meta.columnWidths.measurementPointCol}
                            style={{ paddingLeft: isMobile ? '0px' : undefined }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <FormLabel className="mp-label" style={{ marginBottom: 0 }}>
                                    {meta.label}
                                    <RequiredIndicator meta={meta} optionalText=" - Optional" />
                                </FormLabel>
                                {meta && meta.showLastValue && (
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <span className="control-label" style={{ textTransform: 'uppercase' }}>
                                            Last reading:{' '}
                                        </span>
                                        <span className="control-label" style={{ fontWeight: 'bold' }}>
                                            {meta.lastReadingData &&
                                            meta.lastReadingData.value !== null &&
                                            meta.lastReadingData.value !== undefined
                                                ? formatDateValue(meta.lastReadingData.value)
                                                : 'N/A'}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <DatetimePortal
                                timeFormat={false}
                                utc={true}
                                dateFormat={meta.dateFormat ? meta.dateFormat : 'DD-MMM-YY'}
                                isValidDate={meta.isValidDate}
                                inputProps={{
                                    disabled: handler().disabled,
                                    placeholder: meta.placeholder,
                                    inputMode: 'none'
                                }}
                                onChange={handler().onChange}
                                defaultValue={momentDate}
                            />
                            <FormControl.Feedback />
                        </Col>
                        {meta.guideText?.trim() && (
                            <Col className="mp-guide" xs={meta.columnWidths.guideCol}>
                                {meta.guideText}
                            </Col>
                        )}
                    </Row>
                </FormGroup>
            </Col>
        );
    },
    longText: (isMobile: boolean) => ({ handler, touched, meta, pristine, errors, submitted }: AbstractControl) => {
        const validationState = FormUtil.getValidationState(pristine, errors, submitted);
        const validationClass =
            validationState === 'success' ? 'has-success' : validationState === 'error' ? 'has-error' : '';
        return (
            <Col xs={meta.colWidth || 12}>
                <FormGroup className={validationClass}>
                    <FormLabel className="mp-label">
                        {meta.label}
                        <RequiredIndicator meta={meta} optionalText=" - Optional" />
                    </FormLabel>
                    <div style={{ paddingBottom: '8px', display: 'flex' }}>
                        <input
                            onChange={meta.showInReportHandler(meta.id)}
                            checked={!meta.showInReport}
                            disabled={handler().disabled}
                            style={{ margin: '0 8px 0 0' }}
                            type="checkbox"
                        />{' '}
                        <label style={{ margin: 0 }}>Hide in Report</label>
                    </div>
                    <React.Suspense fallback={<div>Loading editor...</div>}>
                        <RTE content={handler().value} setContent={handler().onChange} readOnly={handler().disabled} />
                    </React.Suspense>
                    <FormControl.Feedback style={{ top: '55px' }} />
                </FormGroup>
            </Col>
        );
    },
    summaryPage: (isMobile: boolean) => ({ handler, touched, meta, pristine, errors, submitted }: AbstractControl) => {
        const validationState = FormUtil.getValidationState(pristine, errors, submitted);
        const validationClass =
            validationState === 'success' ? 'has-success' : validationState === 'error' ? 'has-error' : '';
        return (
            <Col xs={12}>
                <FormGroup className={validationClass}>
                    <FormLabel className="mp-label">
                        {meta.label}
                        <RequiredIndicator meta={meta} optionalText=" - Optional" />
                    </FormLabel>
                    <React.Suspense fallback={<div>Loading editor...</div>}>
                        <RichTextEditor
                            onChange={handler().onChange}
                            initialContent={meta.initialContent}
                            readOnly={handler().disabled}
                            showNormalToolbar={meta.showNormalToolbar}
                            charLimit={meta.charLimit}
                            overrideRTEText={meta.overrideRTEText}
                            resetOverrideRTEText={meta.resetOverrideRTEText}
                        />
                    </React.Suspense>
                    <FormControl.Feedback style={{ top: '55px' }} />
                </FormGroup>
            </Col>
        );
    }
};
